import {
	PropositionProductType,
	ReceiptPaymentType,
	ReceiptStatus,
	ReceiptType,
	ReceiptsCountSchema,
	receiptsCountSchema,
} from "@dune/vars";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { privateAxios } from "../axios";
import {
	Nullable,
	OfferPricingOutput,
	PaginatedResponse,
	ReceiptExtendedInfo,
} from "@dune/types";

export const receiptKeys = {
	all: ["receipts"] as const,
	lists: () => [...receiptKeys.all, "list"] as const,
	list: (filters: unknown) => [...receiptKeys.lists(), filters] as const,
	detail: (id: string | number) => [...receiptKeys.all, id] as const,
	byProposition: (propositionId: number) =>
		[...receiptKeys.all, "byProposition", propositionId] as const,
};

interface CreateReceiptData {
	reference: string;
	propositionId: number;
	type: ReceiptType;
	productType: PropositionProductType;
	issueDate: string;
	duneManagementFeesHT?: number | null;
	duneManagementFeesTaxes?: number | null;
	duneManagementFeesTTC?: number | null;
	intermediaryManagementFeesHT?: number | null;
	intermediaryManagementFeesTaxes?: number | null;
	intermediaryManagementFeesTTC?: number | null;
	brokerCommission?: number | null;
	brokerRemuneration?: number | null;
	doDetails?: Nullable<Partial<OfferPricingOutput<"DO", "INITIAL">>>;
	mrtDetails?: Nullable<Partial<OfferPricingOutput<"MRT", "INITIAL">>>;
	trcDetails?: Nullable<Partial<OfferPricingOutput<"TRC", "INITIAL">>>;
}

interface UpdateReceiptData {
	id: number;
	reference: string;
	type: ReceiptType;
	productType: PropositionProductType;
	issueDate: string;
	duneManagementFeesHT?: number | null;
	duneManagementFeesTaxes?: number | null;
	duneManagementFeesTTC?: number | null;
	intermediaryManagementFeesHT?: number | null;
	intermediaryManagementFeesTaxes?: number | null;
	intermediaryManagementFeesTTC?: number | null;
	brokerCommission?: number | null;
	brokerRemuneration?: number | null;
	doDetails?: Nullable<Partial<OfferPricingOutput<"DO", "INITIAL">>>;
	mrtDetails?: Nullable<Partial<OfferPricingOutput<"MRT", "INITIAL">>>;
	trcDetails?: Nullable<Partial<OfferPricingOutput<"TRC", "INITIAL">>>;
}

interface CreateReceiptPaymentData {
	receiptId: number;
	paymentDate: string;
	amount: number;
	type: ReceiptPaymentType;
	paymentReference: string;
}

interface UpdateReceiptPaymentData {
	id: number;
	receiptId: number;
	paymentDate: string;
	amount: number;
	type: ReceiptPaymentType;
	paymentReference: string;
}

interface DeleteReceiptPaymentData {
	paymentId: number;
	receiptId: number;
}

const createReceipt = async (data: CreateReceiptData): Promise<void> => {
	await privateAxios.post("/receipts", data);
};

export const useCreateReceiptMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createReceipt,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: receiptKeys.all });
		},
	});
};

const createReceiptFromProposition = async (
	propositionId: number,
): Promise<void> => {
	await privateAxios.post(`/receipts/from-proposition/${propositionId}`);
};

export const useCreateReceiptFromPropositionMutation = (
	propositionId: number,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => createReceiptFromProposition(propositionId),
		onSuccess: async () => {
			queryClient.invalidateQueries({ queryKey: receiptKeys.all });
		},
	});
};

const updateReceipt = async (data: UpdateReceiptData): Promise<void> => {
	await privateAxios.put("/receipts", data);
};

export const useUpdateReceiptMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateReceipt,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: receiptKeys.all,
			});
		},
	});
};

const createReceiptPayment = async (
	data: CreateReceiptPaymentData,
): Promise<void> => {
	await privateAxios.post("/receipts/payment", data);
};

export const useCreateReceiptPaymentMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createReceiptPayment,
		onSuccess: (_, variables) => {
			// Invalidate the specific receipt detail to refresh payments
			queryClient.invalidateQueries({
				queryKey: receiptKeys.detail(variables.receiptId),
			});
			// Also invalidate all receipts lists
			queryClient.invalidateQueries({
				queryKey: receiptKeys.lists(),
			});
		},
	});
};

const updateReceiptPayment = async (
	data: UpdateReceiptPaymentData,
): Promise<void> => {
	await privateAxios.put("/receipts/payment", data);
};

export const useUpdateReceiptPaymentMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateReceiptPayment,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: receiptKeys.all,
			});
		},
	});
};

const deleteReceiptPayment = async (
	data: DeleteReceiptPaymentData,
): Promise<void> => {
	await privateAxios.delete(
		`/receipts/payment/${data.paymentId}?receiptId=${data.receiptId}`,
	);
};

export const useDeleteReceiptPaymentMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteReceiptPayment,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: receiptKeys.all,
			});
		},
	});
};

interface UpdateReceiptStatusData {
	id: number;
	status: ReceiptStatus;
}

const updateReceiptStatus = async (
	data: UpdateReceiptStatusData,
): Promise<void> => {
	await privateAxios.patch(`/receipts/${data.id}/status`, {
		status: data.status,
	});
};

export const useUpdateReceiptStatusMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateReceiptStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: receiptKeys.all,
			});
		},
	});
};

const fetchAllReceipts = async (filters?: {
	status?: ReceiptStatus | "all" | string | null;
	type?: ReceiptType | "all" | string | null;
	query?: string | null;
	project?: string | null;
	page?: number;
	limit?: number;
	brokerSocietyId?: number;
}): Promise<PaginatedResponse<ReceiptExtendedInfo>> => {
	const params = new URLSearchParams();

	if (filters?.page) {
		params.append("page", String(filters.page));
	}
	if (filters?.limit) {
		params.append("limit", String(filters.limit));
	}

	if (filters?.status && filters.status !== "all") {
		const statusUpper = String(filters.status).toUpperCase();
		const backendStatus =
			statusUpper === "PENDING"
				? "PENDING"
				: statusUpper === "PAID"
					? "PAID"
					: statusUpper === "REFUNDED" || statusUpper === "REFUND"
						? "REFUNDED"
						: statusUpper === "CANCELLED"
							? "CANCELLED"
							: undefined;
		if (backendStatus) params.append("status", backendStatus);
	}

	if (filters?.type && filters.type !== "all") {
		const typeUpper = String(filters.type).toUpperCase();
		const backendType =
			typeUpper === "AFN" || typeUpper === "NEW_BUSINESS"
				? "NEW_BUSINESS"
				: typeUpper === "AVT" || typeUpper === "AMENDMENT"
					? "AMENDMENT"
					: typeUpper === "ANNUL" || typeUpper === "CANCELLATION"
						? "CANCELLATION"
						: undefined;
		if (backendType) params.append("type", backendType);
	}
	if (filters?.query && filters.query.trim() !== "") {
		params.append("query", filters.query);
	}
	if (filters?.project && filters.project !== "all") {
		params.append("project", filters.project);
	}
	if (filters?.brokerSocietyId != null) {
		params.append("brokerSocietyId", String(filters.brokerSocietyId));
	}

	const { data } = await privateAxios.get<
		PaginatedResponse<ReceiptExtendedInfo>
	>(`/receipts?${params.toString()}`);

	return data;
};

export const useReceiptsQuery = (filters?: {
	status?: ReceiptStatus | "all" | string | null;
	type?: ReceiptType | "all" | string | null;
	query?: string | null;
	project?: string | null;
	page?: number;
	limit?: number;
	brokerSocietyId?: number;
}) => {
	return useQuery({
		queryKey: receiptKeys.list(filters || {}),
		queryFn: () => fetchAllReceipts(filters),
		placeholderData: keepPreviousData,
	});
};

const fetchReceiptsByProposition = async (propositionId: number) => {
	const { data } = await privateAxios.get<ReceiptExtendedInfo[]>(
		`/receipts/proposition/${propositionId}`,
	);

	return data;
};

const fetchReceiptCounts = async (): Promise<ReceiptsCountSchema> => {
	const response = await privateAxios.get("/receipts/counts");

	const parseResult = receiptsCountSchema.safeParse(response.data);
	if (!parseResult.success) {
		throw new Error("Invalid receipt data");
	}

	return parseResult.data;
};

const fetchReceiptById = async (id: string | number) => {
	const { data } = await privateAxios.get<ReceiptExtendedInfo>(
		`/receipts/${id}`,
	);

	return data;
};

export const useReceiptsByPropositionQuery = (propositionId: number) => {
	return useQuery({
		queryKey: receiptKeys.byProposition(propositionId),
		queryFn: () => fetchReceiptsByProposition(propositionId),
		enabled: !!propositionId,
	});
};

export const useReceiptCountsQuery = (options?: { enabled?: boolean }) => {
	return useQuery({
		queryKey: [...receiptKeys.all, "counts"],
		queryFn: fetchReceiptCounts,
		enabled: options?.enabled ?? true,
	});
};

export const useReceiptQuery = (id: string | number | undefined) => {
	return useQuery({
		// biome-ignore lint/style/noNonNullAssertion:
		queryKey: receiptKeys.detail(id!),
		// biome-ignore lint/style/noNonNullAssertion:
		queryFn: () => fetchReceiptById(id!),
		enabled: !!id,
	});
};
